import ProductIncludedExcluded from '@/features/accommodation/ui/ListItemContent/IncludedExcluded';
import { useActivityState } from '@/features/product/hooks/useActivityState';
import ProductListItemContentErrorBoundary from '@/features/product/ui/ErrorBoundary';
import ProductOptionGroup from '@/features/product/ui/OptionGroup';
import ProductQuantity from '@/features/product/ui/Quantity';
import ProductSelectDate from '@/features/product/ui/SelectDate';
import ProductTotal from '@/features/product/ui/Total';
import { cn } from '@/lib/utils';

interface IProps {
  idx: string;
  shouldShow: boolean;
}
export default function ActivityListItemContent({ idx, shouldShow }: IProps) {
  return (
    <ProductListItemContentErrorBoundary>
      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          shouldShow ? 'mb-5 max-h-[9999px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div
          className={cn(
            'transform rounded-3xl border-2 border-[#E9ECEF] bg-white transition-all duration-500 ease-in-out sm:rounded-[28px]',
            shouldShow ? 'translate-y-0 scale-100' : '-translate-y-4 scale-95',
          )}
        >
          <div
            className={cn(
              'p-5 transition-all delay-100 duration-700 sm:p-8',
              shouldShow ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
            )}
            data-testid='content-container'
          >
            <ContentBoundary idx={idx} shouldShow={shouldShow} />
          </div>
        </div>
      </div>
    </ProductListItemContentErrorBoundary>
  );
}

function ContentBoundary({
  idx,
  shouldShow,
}: Omit<IProps, 'selectedIdx' | 'isExpanded'> & { shouldShow: boolean }) {
  const {
    t,
    activityData,

    currentMonth,
    selectedDate,
    handleDateClick,
    handleChangeMonth,

    quantity,
    handleUpdateQuantity,

    selectedOptionGroupList,
    handleSelectOption,

    totalBasePrice,
    totalOptionPrice,
    totalAmount,

    addActivity,
  } = useActivityState(idx, shouldShow);

  return (
    <>
      <ProductIncludedExcluded
        included={activityData?.included}
        excluded={activityData?.excluded}
      />

      <ProductSelectDate
        currentMonth={currentMonth}
        selectedDate={selectedDate}
        availableStartDate={activityData?.startDate}
        availableEndDate={activityData?.endDate}
        handleDateClick={handleDateClick}
        handleChangeMonth={handleChangeMonth}
      />

      <ProductOptionGroup
        optionGroupList={activityData?.optionGroupList}
        selectedOptionGroupList={selectedOptionGroupList}
        handleSelectOption={handleSelectOption}
      />

      <ProductQuantity
        title={t('quantity')}
        quantityName={t('perItem')}
        quantityDescription={`${t('maxPurchaseCount')}: ${activityData?.maxPurchaseCount}`}
        quantity={quantity}
        maxQuantity={Math.min(
          activityData?.availableCount || 0,
          activityData?.maxPurchaseCount || 0,
        )}
        price={activityData?.price}
        updateQuantity={handleUpdateQuantity}
        isRequired
      />

      <ProductTotal
        totalList={[
          {
            label: t('basePrice'),
            price: totalBasePrice,
          },
          {
            label: t('optionPrice'),
            price: totalOptionPrice,
          },
        ]}
        totalAmount={totalAmount}
        addItemLabel={t('addActivity')}
        addItem={addActivity}
      />
    </>
  );
}
